import type { SelectedZyVersionFilter } from '@/lib/zy-tiptap-utils';
import type { CommandProps } from '@tiptap/core';
import { Extension } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { createZyVersionDeletePlugin } from './zyversion-delete-plugin';
import { ZyVersionBlock, ZyVersionBlockHeadless, ZyVersionInline, ZyVersionInlineHeadless } from './zyversion-extension';
import { createZyVersionFilterPlugin } from './zyversion-filter-plugin';
import { createZyVersionRegistryPlugin } from './zyversion-registry-plugin';
import { createZyVersionSelectionPlugin } from './zyversion-selection-plugin';
export { ZyVersionBlockNodeView } from './zyversion-block-node-view';
export { ZyVersionInlineNodeView } from './zyversion-inline-node-view';

export interface ZyVersionOptions {
    onHiddenZyVersionDeleted: (node: PMNode) => void;
    versionFilter?: SelectedZyVersionFilter;
    allowedVersions?: string[];
}

declare module '@tiptap/core' {
    interface Storage {
        zyVersion?: { versionFilter: SelectedZyVersionFilter; allowedVersions: string[] };
    }
}

const skipMeta = ['composition', 'toc'];

export const ZyVersionHeadless = Extension.create<ZyVersionOptions>({
    name: 'zyVersion',

    addOptions() {
        return {
            onHiddenZyVersionDeleted: () => {},
            versionFilter: null,
            allowedVersions: [],
        };
    },

    addStorage() {
        return {
            versionFilter: this.options.versionFilter ?? null,
            allowedVersions: this.options.allowedVersions ?? [],
        };
    },

    addExtensions() {
        return [ZyVersionBlockHeadless, ZyVersionInlineHeadless];
    },

    addCommands() {
        return {
            setVersionFilter:
                (versionFilter: SelectedZyVersionFilter) =>
                ({ editor, tr, dispatch }: CommandProps) => {
                    if (editor.storage.zyVersion) {
                        editor.storage.zyVersion.versionFilter = versionFilter;
                    }
                    if (dispatch) {
                        skipMeta.forEach(meta => tr.setMeta(meta, true));
                        tr.setMeta('zyVersionFilter', true);
                    }
                    return true;
                },
        };
    },

    addProseMirrorPlugins() {
        const { onHiddenZyVersionDeleted } = this.options;
        const getVersionFilter = () => this.storage.versionFilter ?? null;
        return [
            createZyVersionFilterPlugin(getVersionFilter),
            createZyVersionRegistryPlugin(),
            createZyVersionSelectionPlugin(getVersionFilter),
            createZyVersionDeletePlugin({ onHiddenZyVersionDeleted, getVersionFilter }),
        ];
    },
});

export const ZyVersion = ZyVersionHeadless.extend({
    addExtensions() {
        return [ZyVersionBlock, ZyVersionInline];
    },
});

export default ZyVersion;
